import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Actions from "store/plan/actions";
import { PageTemplate, Header } from "components";
import { AddDrugsStepper as Stepper, AddDrugsSteps as Steps, AddDrugsFooter as Footer } from "components";
import { Formik } from "formik";
import { Form } from "formik-antd";
import initialValues from "./utils/initialValues";
import { withFormikDevtools } from "formik-devtools-extension";
import { message } from "antd";
import { upsertPBMs } from "services/api/pbms.api";
import { updatePlans } from "services/api/form.api";
import { updateReportDrugs } from "services/api/admin.api";
import { getToken } from "services/utils";
import { getRole, isProfessional } from "services/api/admin.api";
import { formatPBMs, formatPlans, formatDrugs } from "./utils/format";
import FormikPersist from "./utils/formikPersist";
import { useHistory, useLocation } from "react-router-dom";
import { loadValues } from "./utils/loadValues";
import { drugsSteps } from "./utils/steps";
import { deleteData } from "indexedDB";

const AddDrugsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { state, search } = useLocation();
  const params = new URLSearchParams(search);
  const paramsReportId = params.get("reportId");
  const [formikInitialValues, setFormikInitialValues] = useState(initialValues);
  const [finishedGetRoleCall, setFinishedGetRoleCall] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);

  const current = useSelector((state) => state.plan.current);
  const username = useSelector((state) => state.profile.username);
  const reportId = state?.reportId || paramsReportId;
  const isMasterReport = state?.isMasterReport;
  const returnToDetailsPage = state?.returnToDetailsPage || (paramsReportId && true);
  const activeTab = state?.activeTab || (paramsReportId && 2);

  let token = {};

  const getValuesFromDatabase = async () => {
    token = await getToken();
    const values = await loadValues({ reportId, token });
    setFormikInitialValues(values);
  };

  useEffect(() => {
    dispatch(Actions.Creators.changeCurrent(0));
    const drugsForm = localStorage.getItem("drugsForm");
    const reportFromLocalStorage = drugsForm?.report;
    reportId && (!drugsForm || !reportFromLocalStorage) && getValuesFromDatabase();
    (async () => {
      const token = await getToken();
      const isAdminResponse = await getRole(token);
      const isProfessionalResponse = await isProfessional(token);
      setIsAllowed(isProfessionalResponse.status === 200 || isAdminResponse.status === 200);
      setFinishedGetRoleCall(true);
    })();
  }, []);

  if ((state == null || Object.keys(state)?.length === 0) && finishedGetRoleCall && !isAllowed) {
    window.location.href = "https://predictabill.com";
    return null;
  }

  const successAndRedirect = () => {
    message.success({
      content: <>{"Recommendation Updated"}</>,
      icon: (
        <span
          role="img"
          aria-label="check-circle"
          className="anticon anticon-check-circle"
          style={{
            color: "#00a19b",
          }}
        >
          <svg
            viewBox="64 64 896 896"
            focusable="false"
            data-icon="check-circle"
            width="1em"
            height="1em"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 01-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z"></path>
          </svg>
        </span>
      ),
    });
    localStorage.removeItem("drugsForm");
    deleteData(reportId);
    if (returnToDetailsPage) {
      history.push(`/optimizeV2/${reportId}`, {
        activeTab,
      });
    } else {
      history.push("/recommendations", {
        reportId,
        username,
      });
    }
  };

  return (
    <PageTemplate isContentHover padding="0 12.5%" header={<Header V2 />}>
      {finishedGetRoleCall && isAllowed && formikInitialValues && (
        <Formik
          enableReinitialize={true}
          initialValues={formikInitialValues}
          validateOnMount={false}
          // validationSchema={{}}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const token = await getToken();
              const formattedPBMs = formatPBMs(values);
              const pbmResult = await upsertPBMs({ formList: formattedPBMs, token });
              if (pbmResult.status === 200) {
                const formattedPlans = formatPlans(values, pbmResult.data.pbms);
                const planResult = await updatePlans({
                  planIds: formattedPlans.map((plan) => plan.externalId),
                  isPublic: false,
                  formList: formattedPlans,
                  token,
                });
                const formattedDrugs = formatDrugs(values, pbmResult.data.pbms);
                const reportResult = await updateReportDrugs({
                  reportId,
                  drugs: formattedDrugs,
                  token,
                });
                planResult.status === 200 && reportResult.status === 200 && successAndRedirect();
                setSubmitting(false);
              } else {
                message.error("Error creating/updating PBMs");
              }
            } catch (e) {
              console.error(e);
            }
          }}
        >
          {(formikProps) => {
            const maxNumberOfPharmacies = formikProps.values.pharmacyManagersTable.reduce(
              (max, current) => Math.max(max, current.typesOfPharmacies || 0),
              0,
            );
            const steps = drugsSteps(maxNumberOfPharmacies);
            withFormikDevtools(formikProps);
            return (
              finishedGetRoleCall && (
                <Form>
                  <Stepper isMasterReport={isMasterReport} steps={steps} />
                  <Steps isMasterReport={isMasterReport} steps={steps} />
                  <Footer isSubmitting={formikProps.isSubmitting} isMasterReport={isMasterReport} steps={steps} />
                  <FormikPersist name="drugsForm" />
                </Form>
              )
            );
          }}
        </Formik>
      )}
    </PageTemplate>
  );
};

export default AddDrugsPage;
